import uPlot from 'uplot';

/**
 * uPlot hover tooltip plugin (2026-08-18).
 *
 * uPlot has no built-in tooltip; the canonical pattern is an absolutely
 * positioned div appended to u.over, moved/flipped in the setCursor hook.
 * Shared by every uPlot chart in the app (TargetPacingCard, ChainWowCard,
 * BranchSalesHistory, SalesPulseCard) so hover feel + styling is identical.
 *
 * The tooltip AUGMENTS the charts — every key number is still readable
 * without hovering (UX rule: no hover-only data on touch; uPlot's cursor
 * also drives this on touch-drag, and the tooltip hides on mouseleave).
 *
 * Styled via the global `.uplot-tooltip` class (styles.css) — scoped CSS
 * cannot reach this dynamically-created DOM.
 */

export type TooltipFormatter = (u: uPlot, idx: number) => string;

export function tooltipPlugin(format: TooltipFormatter): uPlot.Plugin {
  const tip = document.createElement('div');
  tip.className = 'uplot-tooltip';
  tip.style.display = 'none';

  let over: HTMLElement | null = null;

  function place(u: uPlot): void {
    const c = u.cursor;
    if (c.idx == null) {
      tip.style.display = 'none';
      return;
    }
    const text = format(u, c.idx);
    tip.textContent = text;
    if (!text.trim()) {
      tip.style.display = 'none';
      return;
    }
    tip.style.display = 'block';

    // Prefer right-of-cursor / above; flip when clipped by the chart box.
    const pad = 10;
    const w = tip.offsetWidth;
    const h = tip.offsetHeight;
    const boundW = over?.clientWidth ?? 0;
    let x = (c.left ?? 0) + pad;
    if (boundW > 0 && x + w > boundW - pad) x = (c.left ?? 0) - w - pad;
    let y = (c.top ?? 0) - h - pad;
    if (y < 0) y = (c.top ?? 0) + pad;
    tip.style.transform = `translate(${x}px, ${y}px)`;
  }

  return {
    hooks: {
      init(u) {
        over = u.over;
        over.appendChild(tip);
        over.addEventListener('mouseleave', () => {
          tip.style.display = 'none';
        });
      },
      setCursor(u) {
        place(u);
      },
      destroy() {
        tip.remove();
        over = null;
      },
    },
  } as uPlot.Plugin;
}

/** Full baht with Thai grouping — tooltip values show exact numbers. */
export function tooltipBaht(v: number): string {
  return `฿${Math.round(v).toLocaleString('th-TH')}`;
}
